import {
  STRIPE_PROCESSING_FEE_BASE,
  STRIPE_PROCESSING_FEE_PERCENT
} from '@/consts/biling.const';
import { InvoiceStatusEnum } from '@/models/invoice.model';
import {
  MedicationInstructionsFrequency,
  MedicationInstructionsQuantity
} from '@/models/medication-instructions.model';
import { ProductFlat } from '@/models/product.model';
import {
  SubscriptionFlat,
  SubscriptionItem,
  SubscriptionStatusEnum
} from '@/models/subscription.model';
import { Interval } from '@/types/common.type';
import capitalize from 'lodash/capitalize';

export const getSubscriptionProductLine = (
  subscription: SubscriptionFlat,
  maxItemsInName: number = 1
): string => {
  const title = subscription.items
    .slice(0, maxItemsInName)
    .map((item) => item.product.name)
    .join(', ');

  if (subscription.items.length - maxItemsInName > 0) {
    return `${title} and ${subscription.items.length - maxItemsInName} more`;
  }
  return `${title}`;
};

export const getSubscriptionMedicationsLine = (
  items: SubscriptionItem[],
  maxItemsInName: number = 1
): string => {
  const title = items
    .slice(0, maxItemsInName)
    .map((item) => item.product.medication.name)
    .join(', ');

  if (items.length - maxItemsInName > 0) {
    return `${title} and ${items.length - maxItemsInName} more`;
  }
  return `${title}`;
};

export const getSubscriptionDisplayInterval = (
  subscription: SubscriptionFlat
): string => {
  const interval = subscription.billingInterval;
  if (interval.count === 1) {
    return capitalize(`${interval.unit}ly`);
  }
  return `Every ${interval.count} ${interval.unit}s`;
};

export const getRecurringPriceDisplay = (
  amount: number,
  interval: Interval
): string => {
  const price = amount.toFixed(2);
  if (interval.count === 1) {
    return `$${price} ${interval.unit}ly`;
  }
  return `$${price} every ${interval.count} ${interval.unit}s`;
};

export const calculateProcessingToCharge = (total: number): number => {
  return Math.ceil(
    (STRIPE_PROCESSING_FEE_PERCENT * total + STRIPE_PROCESSING_FEE_BASE) /
      (1 - STRIPE_PROCESSING_FEE_PERCENT)
  );
};

export const calculateSubscriptionItemInterval = (
  product: ProductFlat,
  instructionsQuantity: MedicationInstructionsQuantity,
  instructionsFrequency: MedicationInstructionsFrequency
): Interval => {
  const quantityMultiplier =
    instructionsFrequency.isPrn && instructionsFrequency.prnQuantityEstimation
      ? instructionsFrequency.prnQuantityEstimation
      : instructionsQuantity.quantityMultiplier;

  return {
    unit: instructionsFrequency.intervalUnit,
    count:
      (product.doseTotalValue * instructionsFrequency.intervalCount) /
      (quantityMultiplier * instructionsFrequency.frequencyMultiplier)
  };
};

export const checkIfRefillAvailable = (
  subscription: SubscriptionFlat,
  allowEarlyRefill: boolean = false
): boolean => {
  if (subscription.isAuto) {
    return false;
  }

  if (
    !subscription.latestInvoiceStatus ||
    ![InvoiceStatusEnum.PAID, InvoiceStatusEnum.VOID].includes(
      subscription.latestInvoiceStatus
    )
  ) {
    return false;
  }

  if (
    ![SubscriptionStatusEnum.ACTIVE, SubscriptionStatusEnum.INACTIVE].includes(
      subscription.status
    )
  ) {
    return false;
  }

  if (!subscription.items.length) {
    return false;
  }

  return allowEarlyRefill || subscription.isCarePlanReadyForCharge;
};
